import React from 'react';
import { compact, findLastIndex, partition, sortBy } from 'lodash';
import { toast } from 'react-toastify';
import type { ScenarioOrder } from '../../../lib/planningData';
import { dToMsOpt, hToMs } from '../../../lib/time';
import type { RequiredNonNullable } from '../../../lib/util';
import { assertKind } from '../../../lib/util';
import type { ViolationIndicatorBasis } from '../../../state/planning/options';
import type { BunkerPriceByGradeName } from '../../hooks/useBunkerPrices';
import type { PnlData } from '../../hooks/usePnlData';
import { buildVoyageFreightIncomeGetter } from '../../shipment/income/income';
import type {
  ScenarioShipmentExtraFreight,
  ScenarioShipmentFreight,
} from '../../Scenario';
import type { ClientId } from '../ClientId';
import { generateClientId } from '../ClientId';
import type {
  OmitFunctions,
  ScheduleData,
  ComputeScheduleMetricsParams,
} from '../Core';
import {
  getFirstEventInLastPortCall,
  orderFullfillsSpecialRequirements,
  vesselFullfillsOrderCargoTypeRequirements,
  FindBestSchedule,
  deriveOrderEvents,
} from '../Core';
import { getVesselCapacities, isMassCapacityAbsent } from '../Vessel';
import { DeriveCapacitiesAndTimes } from '../DeriveCapacitiesAndTimes';
import type { TCOrderEntry, Entry } from '@seaber/common/core/entries';
import { isTCOrderEntry, EventCategory } from '@seaber/common/core/entries';
import type { SuggestionPerformanceMetrics } from '../Core.voyage-metrics';
import {
  getTargetShipmentForSuggestion,
  getSuggestionTCEDelta,
  countTotalMassDistance,
  computeSuggestionPerformanceMetrics,
  calculateCostPerTonMile,
} from '../Core.voyage-metrics';
import type {
  ScheduleDataContext,
  CalculationConfig,
} from '../ScheduleDataContext';
import type { BestSchedule, CargoAllocationOptions } from './Suggestions';
import { CreateAllCombinations } from './Suggestions';
import { applyFreightPrioritizedFill } from './applyFreightPrioritizedFill';
import type { TCTimelineEvent, TCOrderEvents, TimeData } from '../TimeData';
import {
  isLoadEvent,
  isCargoEventTimeData,
  isDischargeEvent,
} from '../TimeData';
import { SuggestionStatusToastContent } from '../ToastContent';
import { dateStringToNum, getOverlappingVoyageEvents } from '../util';
import type { Vessel } from '../Vessel';
import type { VesselSchedule } from '../VesselSchedule';
import type { VoyageSuggestionParams } from './VoyageSuggestionParams';
import { Objective } from './VoyageSuggestionParams';
import type {
  OrderId,
  ScenarioTimeline_ShipFragment,
  ShipId,
} from '../../../graphql';
import type { DefaultMap } from '@seaber/common/lib/collections';
import type { OrderViolationLimits } from '../../ScenarioImprovementParams';
import type { ScheduleMetrics } from '../scheduleMetrics';
import type { VesselExclusionItem } from '../../hooks/useAssortmentExclusions';
import { getVoyageSuggestionsInWorker } from '../PlanningAssistantWorkerMaster';
import type { CalculateTimeDataPnl } from '../../pnl/ui/context';
import { getPnlProcessorVessel } from '../../pnl/context/context';
import type { useSpecialRequirements } from '../../hooks/useSpecialRequirements';

export type GetSuggestionsParams = Parameters<typeof getSuggestions>;

export async function getSuggestions(
  allItems: AssignableItem[],
  vessels: readonly Vessel[],
  context: ScheduleDataContext,
  schedule: readonly Entry[],
  eventsByVessel: Map<number, VesselSchedule>,
  config: CalculationConfig,
  selectedOrderIds: number[],
  inputs: GetSuggestionsInputs,
  pnlData: PnlData,
  calculateTimeDataPnl: CalculateTimeDataPnl,
  bunkerPriceByGradeName: BunkerPriceByGradeName,
  estimateIdOrScenario: ClientId | 'scenario',
  {
    onNewSuggestions,
    shipmentFreightByShipmentClientId,
    shipmentExtraFreightByShipmentClientId,
  }: {
    /** @param pctDone 0-100 */
    onNewSuggestions?: (
      vesselId: ShipId,
      suggestions: Suggestion[],
      pctDone: number,
    ) => void;
    /** The scenario's shipment-freight maps. Passed so suggestion income ranking matches
     * the committed board — orders billed at shipment level rank out of the per-order fill. */
    shipmentFreightByShipmentClientId?: Map<ClientId, ScenarioShipmentFreight>;
    shipmentExtraFreightByShipmentClientId?: Map<
      ClientId,
      ScenarioShipmentExtraFreight[]
    >;
  } = {},
): Promise<RequiredNonNullable<SuggestionResult>> {
  const objective = inputs.objective;
  const voyageTypes = inputs.voyageTypes;
  const specialRequirements = inputs.specialRequirements;

  const violationLimitsMs = {
    load: {
      late: dToMsOpt(inputs.violationLimitsD.load.late) ?? null,
      early: dToMsOpt(inputs.violationLimitsD.load.early) ?? null,
    },
    discharge: {
      late: dToMsOpt(inputs.violationLimitsD.discharge.late) ?? null,
      early: dToMsOpt(inputs.violationLimitsD.discharge.early) ?? null,
    },
  };

  const maximumTotalDelayMs = Math.max(
    hToMs(inputs.maxAllowedTotalDelayH),
    violationLimitsMs.load.late ?? 0,
    violationLimitsMs.load.early ?? 0,
    violationLimitsMs.discharge.late ?? 0,
    violationLimitsMs.discharge.early ?? 0,
  );

  const suggestionsByVessel: SuggestionsByVessel = new Map();

  const logs: SuggestionLog = {
    availableOrderIds: new Set(allItems.flat().map((o) => o.id)),
    logEntries: [],
    logsByVessel: new Map(),
  };

  if (!vessels.length || !allItems.length) {
    return {
      selectedOrderIds,
      suggestionsByVessel,
      isCompleted: true,
      logs,
      estimateIdOrScenario,
    };
  }
  const insertAfter = inputs.insertAfter;
  if (vessels.length > 1 && (inputs.insertBefore || insertAfter)) {
    // The behavior could possibly be a bit unexpected, so making sure we don't call the function like this
    throw new Error(
      `insertBefore & insertAfter parameters are not compatible with multiple vessels`,
    );
  }

  const orderById: Map<OrderId, ScenarioOrder> = new Map();

  for (const vessel of vessels) {
    const events = eventsByVessel.get(vessel.id);
    if (!events) continue;
    for (const event of events) {
      if (isLoadEvent(event)) {
        const { order } = event;
        orderById.set(order.id, order);
      }
    }
  }

  for (const order of allItems.flat()) {
    orderById.set(order.id, order);
  }

  // Build the income getter through the same shared builder as the committed derive, with
  // the scenario's shipment-freight maps, so suggestion ranking matches the committed board — orders
  // billed at shipment level rank out of the per-order fill (design §4).
  const getIncomeForLoadedAmount = buildVoyageFreightIncomeGetter(
    orderById,
    pnlData.incomingVoyageChartersById,
    config.currencyRates,
    config.defaultCurrency,
    bunkerPriceByGradeName,
    pnlData.worldscaleFreightRates,
    shipmentFreightByShipmentClientId,
    shipmentExtraFreightByShipmentClientId,
  );

  const statusToastId = 'compute-voyage-suggestions';
  toast.info('Computing voyage suggestions, this can take a while...', {
    toastId: statusToastId,
    isLoading: true,
  });

  let stopRequested = false;

  const {
    assortmentExclusions: { vesselExclusionsMap },
  } = config;

  const [validItems, pastItems] = partition(allItems, (orders) =>
    orders.every(
      (order) =>
        !order.cancelling ||
        dateStringToNum(order.cancelling) > inputs.firstAllowedTime,
    ),
  );

  if (pastItems.length > 0) {
    logs.logEntries.push({
      orderIds: pastItems.flatMap((orders) => orders.map((o) => o.id)),
      stage: 'preprocessing',
      reason: 'laycan_in_past',
    });
  }

  for (let i = 0; i < vessels.length && !stopRequested; i++) {
    const vessel = vessels[i];

    const vesselLogs: SuggestionLogTree = {
      depth: 0,
      logEntries: [],
      branches: [],
    };
    logs.logsByVessel.set(vessel.id, vesselLogs);

    if (vessel.inactive) continue;
    // spec: specs/constraints/automatic-planning-mass-capacity.md — skipping the vessel here rather
    // than inside one generator keeps every generator this loop reaches subject to the rule.
    if (isMassCapacityAbsent(vessel)) continue;
    const events = eventsByVessel.get(vessel.id);
    if (!events) continue;

    const vesselAttributes = compact(
      vessel.vesselAttributesAssociationsByShipId.nodes.map(
        (a) => a?.vesselAttributeByVesselAttributeId,
      ),
    );

    const items = filterValidOrdersToVessel(
      validItems,
      vessel,
      vesselAttributes,
      vesselExclusionsMap,
      inputs,
      vesselLogs,
      specialRequirements,
    );

    if (
      events.length === 0 &&
      voyageTypes.includes('new_voyage_between') &&
      voyageTypes.length === 1
    ) {
      vesselLogs.logEntries.push({
        stage: 'preprocessing',
        orderIds: items.flatMap((orders) => orders.map((o) => o.id)),
        reason: 'incompatible_setting',
      });
      continue;
    }

    // Any order that passed the filter are valid from "preprocessing" step
    vesselLogs.logEntries.push({
      orderIds: items.flatMap((orders) => orders.map((o) => o.id)),
      stage: 'preprocessing',
      reason: 'is_valid',
    });

    const { massCapacity: vesselMassCapacity } = getVesselCapacities(vessel);
    const massCapacity = vesselMassCapacity ?? Infinity;

    const originalResults = DeriveCapacitiesAndTimes(
      vessel,
      events,
      context,
      config,
      { calculateCosts: true },
      inputs.firstAllowedTime,
    );
    const originalTimeData = originalResults.timeData;

    // Overlapping: suggestions must start after last load, before last discharge
    const overlappingBounds = voyageTypes.includes('overlapping')
      ? getOverlappingBounds(events, originalTimeData, inputs.firstAllowedTime)
      : null;

    const bestSchedules: NonNullable<BestSchedule>[] = [];
    /** Track which bestSchedules came from overlapping (need new shipment ID) */
    const overlappingSchedules = new Set<NonNullable<BestSchedule>>();

    const maxOrdersPerVoyage = inputs.numOrders;
    const lastPCStartEvent = getFirstEventInLastPortCall(events);

    // Re-derive one candidate with the income-prioritized fill (each banded order seeded at
    // band floor, then raised toward max by marginal freight income), mutating its timeData/totals/inputs
    // in place. The overlay is a transient local Map keyed by OrderId, consumed only by this re-derive
    // and never written to the TimeDataCache, so no committed scenario event and no other candidate is
    // affected. Skip candidates with no banded assignment order (perf guard). Applied to the final best
    // schedules AND to the worker's streamed intermediate results, so in-progress banded suggestions show
    // the filled amounts and freight-aware ordering instead of the base forward-greedy fill that the
    // final result would otherwise replace.
    const applyIncomePrioritizedFill = (
      best: NonNullable<BestSchedule>,
      candidateNow: number,
    ): void => {
      const raisableOrderIds = new Set(
        best.combination.assignments
          .map(({ order }) => order)
          .filter((o) => o.cargoRanges.mass.max > o.cargoRanges.mass.min)
          .map((o) => o.id),
      );
      if (raisableOrderIds.size === 0) return;
      const filled = applyFreightPrioritizedFill({
        vessel,
        schedule: best.combination.schedule,
        context,
        config,
        now: candidateNow,
        baseResult: {
          timeData: best.timeData,
          totals: best.totals,
          inputs: best.inputs,
        },
        getIncomeForLoadedAmount,
        raisableOrderIds,
      });
      best.timeData = filled.timeData;
      best.totals = filled.totals;
      best.inputs = filled.inputs;
    };

    for (const vt of voyageTypes) {
      const isOverlapping = vt === 'overlapping';
      const vtInsertInBallast =
        vt === 'new_voyage_between' || vt === 'new_voyage_open_position';
      const vtAddToExisting = vt === 'existing_voyage' || isOverlapping;
      const vtBetweenVoyages = vt === 'new_voyage_between';
      const vtOpenPosition = vt === 'new_voyage_open_position';

      const vtFirstAllowedTime =
        isOverlapping && overlappingBounds
          ? overlappingBounds.firstAllowedTime
          : inputs.firstAllowedTime;
      // Worker still uses single voyageType
      const workerVoyageType = isOverlapping ? 'existing_voyage' : vt;

      if (maxOrdersPerVoyage > 1) {
        const suggestionResult = await getVoyageSuggestionsInWorker(
          async (status) => {
            const ratioOfVesselDone = status.workDone / status.workTotal;
            const pctPerVessel = 100 / vessels.length;
            const totalPctDone = Math.round(
              (i / vessels.length) * 100 + ratioOfVesselDone * pctPerVessel,
            );

            if (status.type === 'progress') {
              toast.update(statusToastId, {
                render: React.createElement(SuggestionStatusToastContent, {
                  text: `Computing voyage suggestions on ${vessel.name}, ${totalPctDone}% done`,
                  onStop: () => {
                    stopRequested = true;
                  },
                }),
                isLoading: true,
              });
            }

            if (onNewSuggestions) {
              // vtFirstAllowedTime is the `now` the worker derived these intermediate candidates with,
              // so the fill's channel-cost timing and delay checks match their selection basis.
              for (const best of status.currentResults) {
                applyIncomePrioritizedFill(best, vtFirstAllowedTime);
              }
              onNewSuggestions(
                vessel.id,
                bestSchedulesToSortedSuggestions(
                  status.currentResults,
                  isOverlapping,
                ),
                totalPctDone,
              );
            }

            return stopRequested;
          },
          vessel,
          context,
          config,
          items,
          events,
          {
            ...inputs,
            firstAllowedTime: vtFirstAllowedTime,
            voyageType: workerVoyageType,
          },
        );
        for (const s of suggestionResult.schedules) {
          bestSchedules.push(s);
          if (isOverlapping) overlappingSchedules.add(s);
        }
        const suggestionLogs = suggestionResult.logs;
        vesselLogs.branches.push(...suggestionLogs.branches);
        vesselLogs.logEntries.push(...suggestionLogs.logEntries);
      } else {
        const minimumIndex = vtOpenPosition
          ? events.length
          : insertAfter
            ? events.findIndex((e) => e === insertAfter) + 1
            : undefined;

        for (const orders of items) {
          const allAvailableOrderIds = new Set(orders.map((o) => o.id));

          const insertBefore =
            inputs.insertBefore ??
            (vtAddToExisting || vtBetweenVoyages ? lastPCStartEvent : null);

          const bestScheduleForVessel = createBestScheduleForOrders(
            orders,
            vessel,
            events,
            originalResults,
            context,
            config,
            {
              delayUntilLD: inputs.delayUntilLD,
              firstAllowedTime: vtFirstAllowedTime,
              maximumTotalDelayMs,
              violationLimitsMs,
              maxBallastDistanceNm: inputs.maxBallastDistanceNm,
              minimumIndex,
              insertBefore,
              objective,
              onlyInsertInBallast:
                vtInsertInBallast || objective === Objective.ShortestBallast,
              assignedOrderIds: new Set(),
              preComputedOriginalAllDerived: originalResults,
              allocationOptions: inputs.allocationOptions,
              respectCargoExclusions: inputs.cargoExclusions === 'exclude',
              respectDraftRestrictions: inputs.draftRestrictions === 'exclude',
              allAvailableOrderIds,
              violationBasis: inputs.violationBasis,
              shipmentId: inputs.addToShipmentId ?? null,
              logEntries: vesselLogs.logEntries,
            },
          );

          if (bestScheduleForVessel) {
            vesselLogs.logEntries.push({
              stage: 'combinations',
              orderIds: orders.map((o) => o.id),
              reason: 'is_valid',
            });
            bestSchedules.push(bestScheduleForVessel);
            if (isOverlapping) overlappingSchedules.add(bestScheduleForVessel);
          } else {
            vesselLogs.logEntries.push({
              stage: 'combinations',
              orderIds: orders.map((o) => o.id),
              reason: 'no_valid_schedule',
            });
          }
        }
      }
    }

    function bestSchedulesToSortedSuggestions(
      bestSchedules: NonNullable<BestSchedule>[],
      isOverlappingRun = false,
    ): Suggestion[] {
      const suggestions = bestSchedules.map((result) => {
        const bestScheduleForVessel = result;
        const assignments = bestScheduleForVessel.combination.assignments;
        const { timeData } = bestScheduleForVessel;

        const suggestionShipmentId =
          isOverlappingRun || overlappingSchedules.has(result)
            ? generateClientId()
            : (inputs.addToShipmentId ??
              getTargetShipmentForSuggestion(
                bestScheduleForVessel.combination,
              ));
        const entries = assignments.map(
          ({ order, loadIndex, dischargeIndex }) => {
            const orderId = order.id;

            const existingEntry = schedule
              .filter(isTCOrderEntry)
              .find((entry) => entry.orderId === orderId);

            const loadTimeData = timeData[loadIndex];
            const dischargeTimeData = timeData[dischargeIndex];
            assertKind(
              loadTimeData,
              isCargoEventTimeData,
              `expected there to be a load and discharge event for suggestions`,
            );
            assertKind(
              dischargeTimeData,
              isCargoEventTimeData,
              `expected there to be a load and discharge event for suggestions`,
            );

            const entry: TCOrderEntry = {
              dbId: existingEntry?.dbId ?? null,
              clientId: existingEntry?.clientId ?? generateClientId(),
              category: EventCategory.TC,
              orderId: order.id,
              vesselId: vessel.id,
              loadingStart: loadTimeData.start,
              loadingStartOverride: null,
              loadingEnd: loadTimeData.end,
              loadingDuration: loadTimeData.end - loadTimeData.start,
              loadingDurationOverride: null,
              loadingSeaDurationOverride: null,
              dischargingStart: dischargeTimeData.start,
              dischargingStartOverride: null,
              dischargingEnd: dischargeTimeData.end,
              dischargingDuration:
                dischargeTimeData.end - dischargeTimeData.start,
              dischargingDurationOverride: null,
              dischargingSeaDurationOverride: null,
              loadingConcurrent: false,
              dischargingConcurrent: false,
              shared: false,
              loadingPortCallId: null,
              dischargingPortCallId: null,
              updatedAt: Date.now(),
              shipmentId: suggestionShipmentId ?? order.shipmentId ?? null,
              cleaningOmitted: false,
            };

            loadTimeData.event.entry = entry;
            dischargeTimeData.event.entry = entry;

            return entry;
          },
        );

        const {
          breakEvenFreight,
          cost,
          totalPortCosts,
          totalChannelCosts,
          totalExtraCosts,
          tcCost,
          tce,
          durationInDays,
          seaDurationInDays,
          portActivitiesInDays,
          suggestionVoyageStart,
          suggestionVoyageEnd,
          income,
          tceDelta,
          missingIncomeOrderIds,
        } = getSuggestionTCEDelta(
          originalTimeData,
          suggestionShipmentId,
          bestScheduleForVessel,
          getIncomeForLoadedAmount,
          calculateTimeDataPnl,
          context,
          {
            outGoingCoaId: null,
            carbonPrice: config.carbonPrice,
            pnlData,
            vessel: getPnlProcessorVessel(vessel),
          },
          'overlapping_voyage',
          orderById,
        );

        const totalMassDistance = countTotalMassDistance(
          timeData,
          context.distances,
        );

        /** Tds added by the new suggestion */
        const newTds = bestScheduleForVessel.timeData.filter(
          (td) =>
            isCargoEventTimeData(td) &&
            bestScheduleForVessel.combination.assignments.find(
              (a) => a.order.id === td.event.order.id,
            ),
        );

        /** Target voyage (voyage(s) that have any overlap with our new assignments) before adding the new suggestion */
        const originalAffectedVoyage = getOverlappingVoyageEvents(
          newTds,
          originalResults.timeData,
        );
        /** Resulting total voyage after adding the new suggestion */
        const affectedVoyage = getOverlappingVoyageEvents(
          newTds,
          bestScheduleForVessel.timeData,
        );

        const voyagePerformanceMetrics = computeSuggestionPerformanceMetrics(
          originalAffectedVoyage,
          affectedVoyage,
          context.distances,
          massCapacity,
        );
        const vesselPerformanceMetrics = computeSuggestionPerformanceMetrics(
          originalResults.timeData,
          bestScheduleForVessel.timeData,
          context.distances,
          massCapacity,
        );

        const suggestion: Suggestion = {
          entries,
          schedule: bestScheduleForVessel,
          cost,
          tcCost,
          massDistance: totalMassDistance,
          tce,
          tceDelta,
          missingIncomeOrderIds: new Set(missingIncomeOrderIds),
          income,
          durationInDays,
          seaDurationInDays,
          portActivitiesInDays,
          suggestionVoyageStart,
          suggestionVoyageEnd,
          ballastDelta:
            bestScheduleForVessel.totals.ballastDistance -
            originalResults.totals.ballastDistance,
          delayDelta:
            bestScheduleForVessel.totals.delays - originalResults.totals.delays,
          bunkerCostDelta:
            bestScheduleForVessel.totals.bunkerCost -
            originalResults.totals.bunkerCost,
          voyagePortCost: totalPortCosts,
          channelCost: totalChannelCosts,
          extraCost: totalExtraCosts,
          portCostDelta:
            bestScheduleForVessel.totals.portCost -
            originalResults.totals.portCost,
          distanceDelta: vesselPerformanceMetrics.delta.distance,
          durationDelta:
            bestScheduleForVessel.totals.totalHours -
            originalResults.totals.totalHours,
          voyagePerformanceMetrics,
          vesselPerformanceMetrics,
          objective,
          shipmentId: inputs.addToShipmentId ?? suggestionShipmentId,
          logs: logs,
          estimateIdOrScenario,
          breakEvenFreight,
        };
        return suggestion;
      });

      suggestions.sort(
        objective === Objective.MinimizeCost
          ? (a, b) => a.cost - b.cost
          : objective === Objective.MaximizeVCP
            ? (a, b) =>
                +b.vesselPerformanceMetrics.delta.vcp -
                +a.vesselPerformanceMetrics.delta.vcp
            : objective === Objective.ShortestDistance
              ? (a, b) => a.distanceDelta - b.distanceDelta
              : objective === Objective.ShortestBallast
                ? (a, b) => a.ballastDelta - b.ballastDelta
                : objective === Objective.MinimizeCostPerTonMile
                  ? (a, b) =>
                      calculateCostPerTonMile(a.cost, a.massDistance) -
                      calculateCostPerTonMile(b.cost, b.massDistance)
                  : (a, b) => b.tce - a.tce,
      );
      return suggestions;
    }

    // Income-prioritized fill toward max on the final best schedules; ranking/display then
    // use the filled amounts. Re-derive with the same `now` each candidate was selected with —
    // overlapping candidates were derived from overlappingBounds.firstAllowedTime (after the existing
    // voyage's last load), not the base firstAllowedTime — since `now` drives channel-cost timing and the
    // event.start >= now delay check, so a mismatched basis would shift the ranking metrics.
    for (const best of bestSchedules) {
      applyIncomePrioritizedFill(
        best,
        overlappingSchedules.has(best) && overlappingBounds
          ? overlappingBounds.firstAllowedTime
          : inputs.firstAllowedTime,
      );
    }

    const suggestions = bestSchedulesToSortedSuggestions(bestSchedules).filter(
      (s) => {
        if (!s.schedule || !overlappingSchedules.has(s.schedule)) return true;
        if (!overlappingBounds?.lastAllowedTime) return true;
        return s.suggestionVoyageStart < overlappingBounds.lastAllowedTime;
      },
    );
    suggestionsByVessel.set(vessel.id, suggestions);
  }

  toast.update(statusToastId, {
    type: 'success',
    render:
      'Voyage suggestions complete' +
      (stopRequested ? ' (stopped by user)' : ''),
    isLoading: false,
    autoClose: 2000,
  });

  if (
    [...suggestionsByVessel.values()].every(
      (suggestions) => suggestions.length === 0,
    )
  ) {
    toast.warn(
      `Could not find any valid suggestions for the given parameters.`,
    );
  }

  return {
    selectedOrderIds,
    suggestionsByVessel,
    isCompleted: true,
    logs,
    estimateIdOrScenario,
  };
}

export function vesselFullfillsAllOrderRequirements(
  vessel: ScenarioTimeline_ShipFragment,
  order: ScenarioOrder,
  specialRequirements: ReturnType<typeof useSpecialRequirements>,
): boolean {
  return (
    orderFullfillsSpecialRequirements(vessel, order, specialRequirements)
      .isFulfilled && vesselFullfillsOrderCargoTypeRequirements(vessel, order)
  );
}

export type CreateBestScheduleForOrdersOptions = {
  /** Should we wait to load/discharge at earliest on layday/rta, or as soon as possible */
  delayUntilLD?: boolean;
  firstAllowedTime: number;
  insertBefore?: TCTimelineEvent | null;
  maximumTotalDelayMs?: number;
  violationLimitsMs: OrderViolationLimits;
  respectCargoExclusions?: boolean;
  respectDraftRestrictions?: boolean;
  minimumIndex?: number;
  objective: Objective;
  onlyInsertInExistingPortCalls?: boolean;
  shipmentId: ClientId | null;
  preComputedOriginalAllDerived?: ScheduleData<TCTimelineEvent>;
  allocationOptions: CargoAllocationOptions;
  /** See deriveOrderEvents:orderEventsCache */
  orderEventsCache?: DefaultMap<number, Map<number, TCOrderEvents>>;
  maxBallastDistanceNm: VoyageSuggestionParams['maxBallastDistanceNm'];
  allAvailableOrderIds: Set<number>;
  violationBasis: ViolationIndicatorBasis;
  logEntries?: SuggestionLogEntry[];
} & (
  | {
      onlyInsertInBallast?: false;
      assignedOrderIds?: Set<number>;
    }
  | {
      onlyInsertInBallast: true;
      /** Orders have already been assigned together with the ones being processed now */
      assignedOrderIds: Set<number>;
    }
);

export type BundledOrders = ScenarioOrder[];
export type AssignableItem = BundledOrders;

/** Assigns all given orders and finds the best schedule greedily. Sorting of the input orders may affect the result.  */
export function createBestScheduleForOrders(
  orders: ScenarioOrder[],
  vessel: Vessel,
  prevAllEvents: TCTimelineEvent[],
  /** Baseline derived values against which we compare values such as delays */
  originalDerived: ScheduleData,
  context: Pick<
    ScheduleDataContext,
    | 'coasById'
    | 'distances'
    | 'portCostEstimateMaps'
    | 'locations'
    | 'portCalls'
    | 'voyageExclusions'
    | 'currentUser'
  >,
  config: OmitFunctions<CalculationConfig>,
  {
    delayUntilLD,
    firstAllowedTime = Date.now(),
    insertBefore,
    maximumTotalDelayMs,
    violationLimitsMs,
    respectCargoExclusions,
    respectDraftRestrictions,
    minimumIndex,
    objective,
    onlyInsertInBallast,
    assignedOrderIds,
    onlyInsertInExistingPortCalls,
    preComputedOriginalAllDerived,
    allocationOptions,
    orderEventsCache,
    maxBallastDistanceNm,
    allAvailableOrderIds,
    violationBasis,
    shipmentId,
    logEntries,
  }: CreateBestScheduleForOrdersOptions,
): BestSchedule | null {
  let result: BestSchedule | null = null;
  let previousAllDerived = preComputedOriginalAllDerived;

  const deriveParams: ComputeScheduleMetricsParams = {
    vessel,
    context,
    config,
    now: firstAllowedTime,
  };

  assignedOrderIds = new Set(assignedOrderIds);
  orders = sortBy(orders, (o) => o.layday);
  for (let i = 0; i < orders.length; ++i) {
    const order = orders[i];
    const orderEvents = deriveOrderEvents(
      order,
      vessel,
      firstAllowedTime,
      {},
      context.locations,
      { delayUntilEarliestLD: delayUntilLD, orderEventsCache, shipmentId },
    );

    if (!orderEvents) {
      console.warn(
        `Could not derive order events for order ${order.id} on vessel ${vessel.id}`,
      );
      return null;
    }

    const maximumIndex = insertBefore
      ? prevAllEvents.findIndex((e) => e === insertBefore) - 1
      : undefined;

    // Derive TimeData. We can skip this on the first iteration if it was already provided.
    if (previousAllDerived?.timeData.length !== prevAllEvents.length) {
      previousAllDerived = DeriveCapacitiesAndTimes(
        vessel,
        prevAllEvents,
        context,
        config,
        {
          skipSwapComputation: true,
          calculateCosts: true,
        },
        firstAllowedTime,
      );
    }

    const combinations = CreateAllCombinations(
      prevAllEvents,
      orderEvents,
      previousAllDerived.timeData,
      {
        onlyInsertInBallast,
        assignedOrderIds,
        onlyInsertInExistingPortCalls,
        minimumIndex,
        maximumIndex,
        firstAllowedTime,
        violationLimitsMs,
        allocationOptions,
      },
    );

    const bestScheduleForOrder = FindBestSchedule(
      combinations,
      deriveParams,
      originalDerived,
      objective,
      allAvailableOrderIds,
      shipmentId,
      violationBasis,
      {
        countIdleFromIndex: minimumIndex,
        maximumDelayDeltaMs: maximumTotalDelayMs,
        maxBallastDistanceNm,
        violationLimitsMs,
        respectCargoExclusions,
        respectDraftRestrictions,
        logEntries,
      },
    );

    if (!bestScheduleForOrder) {
      return null;
    }

    result = bestScheduleForOrder;
    prevAllEvents = bestScheduleForOrder.combination.schedule;
    for (const order of orders) {
      assignedOrderIds.add(order.id);
    }
  }

  if (result) {
    // CreateAllCombinations & FindBestSchedule only consider one order, update the result to include all orders.
    result.combination.assignments = updateAssignmentsInBestSchedule(
      orders,
      result,
    );
  }

  return result;
}

export function updateAssignmentsInBestSchedule(
  newAssignedOrders: ScenarioOrder[],
  bestSchedule: NonNullable<BestSchedule>,
) {
  return newAssignedOrders.map((order) => {
    // Using lastIndex because new assignments are usually closer to the end
    const loadIndex = bestSchedule.combination.schedule.findLastIndex(
      (e) => isLoadEvent(e) && e.order.id === order.id,
    );
    const dischargeIndex = bestSchedule.combination.schedule.findLastIndex(
      (e) => isDischargeEvent(e) && e.order.id === order.id,
    );
    if (loadIndex === -1 || dischargeIndex === -1) {
      throw new Error(
        `Expected to find load and discharge index for all assigned orders`,
      );
    }
    return {
      order,
      loadIndex,
      dischargeIndex,
    };
  });
}

// Base types for different stages of order processing
export type OrderPreprocessingLog = {
  stage: 'preprocessing';
} & (
  | { reason: 'order_vessel_requirements' }
  | { reason: 'cargo_exclusion' }
  | { reason: 'crane_capacity_violation' }
  | { reason: 'laycan_in_past' }
  | { reason: 'incompatible_setting' } // e.g. 'between_voyages' but not idle slots available
  | { reason: 'is_valid' }
);

export type OrderShortlistingLog = {
  stage: 'shortlisting';
} & (
  | { reason: 'excessive_idle'; arrivalTime: number }
  | { reason: 'laycan_unreachable'; arrivalTime: number }
  | { reason: 'duplicate_port_pair' }
  | { reason: 'no_possible_ports' }
  | { reason: 'insufficient_capacity' }
  | { reason: 'excessive_ballast_distance' }
  | { reason: 'distance_from_ports' }
  | { reason: 'selected' }
);

/** Log entries for reasons on individual solution schedule (combination) candidates */
export type SchedulingLog = {
  stage: 'scheduling';
} & (
  | { reason: 'metrics_violation'; violations: ScheduleMetrics['violations'] }
  | { reason: 'not_best_schedule' }
  | { reason: 'selected' }
);

/** Log entries for reasons on a combination of orders */
export type CombinationsLog = {
  stage: 'combinations';
} & (
  | { reason: 'no_valid_schedule' }
  | { reason: 'is_valid' }
  | { reason: 'identical_voyage' }
);

export type SolutionsLog = {
  stage: 'solutions';
} & ({ reason: 'low_solution_vcp' } | { reason: 'selected' });

// The main SuggestionLogEntry type
export type SuggestionLogEntry = { orderIds: number[] } & (
  | OrderPreprocessingLog
  | OrderShortlistingLog
  | SchedulingLog
  | CombinationsLog
  | SolutionsLog
);

export type SuggestionLogReason = SuggestionLogEntry['reason'];

export type SuggestionLog = {
  availableOrderIds: Set<number>;
  /** Logs happening on top level, outside vessels iteration */
  logEntries: SuggestionLogEntry[];
  /** Logs happening on a per-vessel basis */
  logsByVessel: Map<number, SuggestionLogTree>;
};

// TODO: maybe create a structure that assigns to pre-defined objects instead of linear log
// e.g. { validOrders: Set<number>, invalidOrders: Map<number, PreprocessingFailure> }
export type SuggestionLogTree = {
  depth: number;
  logEntries: SuggestionLogEntry[];
  branches: SuggestionLogTree[];
};

export type Suggestion = {
  entries: TCOrderEntry[];
  tce: number;
  tceDelta: number | null;
  missingIncomeOrderIds: Set<number>;
  cost: number;
  tcCost: number;
  income: number;
  durationInDays: number;
  seaDurationInDays: number;
  portActivitiesInDays: number;
  suggestionVoyageStart: number;
  suggestionVoyageEnd: number;
  massDistance: number;
  voyagePerformanceMetrics: SuggestionPerformanceMetrics;
  vesselPerformanceMetrics: SuggestionPerformanceMetrics;
  ballastDelta: number;
  delayDelta: number;
  bunkerCostDelta: number;
  voyagePortCost: number;
  channelCost: number;
  extraCost: number;
  portCostDelta: number;
  distanceDelta: number;
  durationDelta: number;
  schedule: BestSchedule;
  objective: Objective;
  shipmentId: ClientId | null | undefined;
  logs: SuggestionLog | null;
  estimateIdOrScenario: ClientId | 'scenario';
  breakEvenFreight: number;
};

export type SuggestionsByVessel = Map<ShipId, Suggestion[]>;

export type SuggestionResult = {
  isCompleted: boolean;
  selectedOrderIds: number[];
  suggestionsByVessel: SuggestionsByVessel;
  logs: SuggestionLog | null;
  estimateIdOrScenario: ClientId | 'scenario';
};

export type GetSuggestionsInputs = VoyageSuggestionParams & {
  addToShipmentId?: ClientId | null;
  /** Only relevant when suggesting for a single vessel. */
  insertAfter?: TCTimelineEvent | null;
  /** Only relevant when suggesting for a single vessel. */
  insertBefore?: TCTimelineEvent | null;
  /** Wait until loading/discharging window? */
  delayUntilLD: boolean;
  allocationOptions: CargoAllocationOptions;
  maxResults?: number;
  violationBasis: ViolationIndicatorBasis;
  specialRequirements: ReturnType<typeof useSpecialRequirements>;
};

/** Assignable items without those that are incompatible with the vessel's attributes.
 * When cargoExclusions option is set to 'warn', we don't drop orders based on that criteria in this step, but those violations will be marked on the timeline. */
function filterValidOrdersToVessel(
  allItems: BundledOrders[],
  vessel: Vessel,
  vesselAttributes: {
    id: number;
  }[],
  vesselExclusionsMap: Map<number, VesselExclusionItem[]>,
  params: Pick<GetSuggestionsInputs, 'cargoExclusions' | 'firstAllowedTime'>,
  logs: SuggestionLogTree,
  specialRequirements: ReturnType<typeof useSpecialRequirements>,
) {
  const [exclusionValid, exclusionInvalid] =
    params.cargoExclusions === 'warn'
      ? [allItems, []]
      : partition(allItems, (orders) =>
          orders.every((order) =>
            vesselAttributes.every((va) => {
              const attrExclusions = vesselExclusionsMap.get(va.id);
              const isAssortmentExcluded = !attrExclusions?.some(
                (e) =>
                  (e.exclusion.__typename === 'Assortment' &&
                    e.exclusion.id === order.assortmentByAssortmentId?.id) ||
                  (e.exclusion.__typename === 'AssortmentGroup' &&
                    e.exclusion.id ===
                      order.assortmentByAssortmentId
                        ?.assortmentGroupByAssortmentGroup?.id),
              );
              return isAssortmentExcluded;
            }),
          ),
        );

  if (exclusionInvalid.length > 0) {
    logs.logEntries.push({
      orderIds: exclusionInvalid.flatMap((orders) => orders.map((o) => o.id)),
      stage: 'preprocessing',
      reason: 'cargo_exclusion',
    });
  }

  const [craneCapacityValid, craneCapacityInvalid] =
    params.cargoExclusions === 'warn'
      ? [exclusionValid, []]
      : partition(exclusionValid, (orders) => {
          const vesselCraneCapacity = vessel.craneCapacity
            ? Number(vessel.craneCapacity)
            : null;
          if (!vesselCraneCapacity) return true;

          return orders.every((order) => {
            const cargoMaxUnitWeight = order.orderDetailsByOrderId.nodes[0]
              ?.cargoMaxUnitWeight
              ? Number(order.orderDetailsByOrderId.nodes[0].cargoMaxUnitWeight)
              : null;
            if (!cargoMaxUnitWeight) return true;

            return vesselCraneCapacity >= cargoMaxUnitWeight;
          });
        });

  if (craneCapacityInvalid.length > 0) {
    logs.logEntries.push({
      orderIds: craneCapacityInvalid.flatMap((orders) =>
        orders.map((o) => o.id),
      ),
      stage: 'preprocessing',
      reason: 'crane_capacity_violation',
    });
  }

  const [requirementsValid, requirementsInvalid] = partition(
    craneCapacityValid,
    (item) =>
      item.every((order) => {
        const fullfillsRequirements = vesselFullfillsAllOrderRequirements(
          vessel,
          order,
          specialRequirements,
        );
        return fullfillsRequirements;
      }),
  );

  if (requirementsInvalid.length > 0) {
    logs.logEntries.push({
      orderIds: requirementsInvalid.flatMap((orders) =>
        orders.map((o) => o.id),
      ),
      stage: 'preprocessing',
      reason: 'order_vessel_requirements',
    });
  }

  return requirementsValid;
}

function getOverlappingBounds(
  events: TCTimelineEvent[],
  timeData: readonly TimeData<TCTimelineEvent>[],
  baseFirstAllowedTime: number,
) {
  const lastLoadIdx = findLastIndex(events, isLoadEvent);
  const lastDischargeIdx = findLastIndex(events, isDischargeEvent);
  return {
    firstAllowedTime: Math.max(
      baseFirstAllowedTime,
      lastLoadIdx >= 0 ? timeData[lastLoadIdx].end : 0,
    ),
    lastAllowedTime:
      lastDischargeIdx >= 0 ? timeData[lastDischargeIdx].start : null,
  };
}

/** Resolves the effective voyageTypes from the modal's radio+checkbox state.
 * When 'new_voyages' radio is selected, returns the checked sub-types.
 * Otherwise returns just the selected radio value. */
export function resolveVoyageTypes(
  params: Pick<VoyageSuggestionParams, 'voyageType' | 'voyageTypes'>,
): VoyageSuggestionParams['voyageTypes'] {
  if (params.voyageType === 'new_voyages') {
    return params.voyageTypes.length > 0
      ? params.voyageTypes
      : ['new_voyage_open_position'];
  }
  if (params.voyageType === 'new_or_existing_voyage') {
    return ['new_or_existing_voyage'];
  }
  return [params.voyageType as VoyageSuggestionParams['voyageTypes'][number]];
}
